import React, { useState } from "react";
import { useOutletContext, useNavigate, useParams } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.autorev.postkaro.app";

function copyToClipboard(text) {
  if (navigator.clipboard) return navigator.clipboard.writeText(text);
  const el = document.createElement("textarea");
  el.value = text;
  el.style.position = "fixed";
  el.style.opacity = "0";
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
  return Promise.resolve();
}

function Code({ children, label = "curl" }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={s.codeWrap}>
      <div style={s.codeHeader}>
        <span style={s.codeLabel}>{label}</span>
        <button
          type="button"
          style={s.codeCopy}
          onClick={() => {
            copyToClipboard(children);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? "✓ Copied" : "Copy"}
        </button>
      </div>
      <pre style={s.pre}>{children}</pre>
    </div>
  );
}

function Method({ m }) {
  return <span style={{ ...s.method, ...(m === "POST" ? s.post : s.get) }}>{m}</span>;
}

/* required is rendered as its own column rather than a footnote: it is the
   first thing anyone writing a request needs, and a marker buried in prose
   gets missed. */
function Params({ rows }) {
  return (
    <div style={s.tableWrap}>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Parameter</th>
            <th style={s.th}>Type</th>
            <th style={s.th}>Required</th>
            <th style={s.th}>Default</th>
            <th style={s.th}>Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} style={s.tr}>
              <td style={s.td}><code style={s.param}>{r.name}</code></td>
              <td style={{ ...s.td, color: "#64748b" }}>{r.type}</td>
              <td style={s.td}>
                {r.required
                  ? <span style={s.reqYes}>Required</span>
                  : <span style={s.reqNo}>Optional</span>}
              </td>
              <td style={{ ...s.td, color: "#64748b" }}>{r.def || "—"}</td>
              <td style={{ ...s.td, color: "#475569" }}>{r.notes}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Section({ id, title, method, path, children }) {
  return (
    <section id={id} style={s.section}>
      <h2 style={s.h2}>{title}</h2>
      {method && (
        <div style={s.endpointRow}>
          <Method m={method} />
          <code style={s.endpoint}>{path}</code>
        </div>
      )}
      {children}
    </section>
  );
}

const NAV = [
  ["how-it-works", "How billing works"],
  ["auth", "Authentication"],
  ["setup", "Setup"],
  ["status", "Subscription Status"],
  ["user-status", "User Status"],
  ["user-subscriptions", "User Subscriptions"],
  ["invoices", "Invoices"],
  ["revoke", "Revoke"],
  ["report", "Report"],
  ["errors", "Errors"],
];

export default function IntegrationGuide() {
  const { app } = useOutletContext();
  const { appId } = useParams();
  const navigate = useNavigate();
  const base = `${API_BASE}/api/v1/${appId}/phonepay`;

  return (
    <div style={s.page}>
      <button onClick={() => navigate(`/apps/${appId}/settings`)} style={s.back}>
        ← Settings
      </button>

      <h1 style={s.h1}>Integration Guide</h1>
      <p style={s.lead}>
        Everything your server needs to put <strong>{app.name}</strong> customers on a PhonePe
        subscription. The URLs below already carry this app's ID — copy them as they are.
      </p>

      <nav style={s.toc}>
        {NAV.map(([id, label]) => (
          <a key={id} href={`#${id}`} style={s.tocLink}>{label}</a>
        ))}
      </nav>

      <Section id="how-it-works" title="How billing works">
        <p style={s.p}>
          You create a subscription. AutoRev collects every payment on it after that.
        </p>
        <div style={s.callout}>
          There is no "charge now" call. Notifying the payer, collecting on the due date,
          retrying a failure and advancing to the next cycle all run inside AutoRev on a
          schedule. To find out what happened, use webhooks or poll the status endpoint.
        </div>
        <p style={s.p}>
          A subscription moves <code style={s.inline}>INITIATED</code> →{" "}
          <code style={s.inline}>ACTIVE</code> once the payer approves the UPI mandate, and stays
          there until it is paused or revoked. Any state ending in{" "}
          <code style={s.inline}>_IN_PROGRESS</code> means PhonePe is still settling a change —
          read it again shortly rather than treating it as final.
        </p>
        <p style={s.p}>
          Two conventions apply everywhere: <strong>money is paise</strong> as an integer
          (<code style={s.inline}>4900</code> is ₹49.00), and <strong>dates are IST</strong>.
        </p>
      </Section>

      <Section id="auth" title="Authentication">
        <p style={s.p}>
          Every request carries this app's ID in the path and your API key as a bearer token.
          Both are on the Settings page under App Credentials.
        </p>
        <Code label="headers">{`Authorization: Bearer YOUR_API_KEY
Content-Type: application/json`}</Code>
        <p style={s.p}>
          The key authorises creating and cancelling mandates on your merchant account. Keep it
          server-side — never in an app binary or a browser bundle. If it leaks, reset it from
          Settings; the old key stops working immediately, so deploy the new one first.
        </p>
        <p style={s.pMuted}>
          App ID and key are checked together. A subscription belonging to another app is
          reported as <code style={s.inline}>404 Subscription not found</code>, never exposed.
        </p>
      </Section>

      <Section id="setup" title="Setup" method="POST" path={`${base}/setup`}>
        <p style={s.p}>
          Creates the mandate and returns the PhonePe intent for the payer to approve. The
          subscription stays <code style={s.inline}>INITIATED</code> until they do.
        </p>
        <Params
          rows={[
            { name: "phone_number", type: "string", required: true, notes: "Exactly 10 digits, no country code" },
            { name: "max_amount", type: "int (paise)", required: true, notes: "Ceiling the payer authorises per debit. Must be > 0" },
            { name: "amount", type: "int (paise)", required: false, def: "app trial amount", notes: "Charged at setup. Required unless the app has a trial amount configured" },
            { name: "frequency", type: "enum", required: false, def: "ON_DEMAND", notes: "DAILY · WEEKLY · FORTNIGHTLY · MONTHLY · BIMONTHLY · QUARTERLY · HALFYEARLY · YEARLY · ON_DEMAND" },
            { name: "amount_type", type: "enum", required: false, def: "VARIABLE", notes: "FIXED or VARIABLE" },
            { name: "trail_days", type: "int", required: false, def: "app trial period, else 0", notes: "Free-trial length. First recurring debit lands this many days after setup" },
            { name: "auth_workflow_type", type: "enum", required: false, def: "TRANSACTION", notes: "TRANSACTION or PENNY_DROP" },
            { name: "product_type", type: "enum", required: false, def: "UPI_INTENT", notes: "UPI_INTENT or UPI_MANDATE" },
            { name: "device_os", type: "enum", required: false, def: "Android", notes: "Android or iOS. Used only for UPI_INTENT" },
            { name: "payment_target_app", type: "string", required: false, def: "null", notes: "Open a specific UPI app for the intent" },
          ]}
        />
        <div style={s.callout}>
          <strong>Two things that are hard to undo.</strong>{" "}
          Set <code style={s.inline}>max_amount</code> to the most you could ever charge in one
          debit, not today's price — raising it later needs a brand-new mandate and a fresh
          approval. And <code style={s.inline}>ON_DEMAND</code>, the default{" "}
          <code style={s.inline}>frequency</code>, has no schedule, so nothing is ever collected
          automatically. Pass a real frequency for recurring billing.
        </div>
        <Code>{`curl -X POST \\
  "${base}/setup" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
  "phone_number": "9876543210",
  "amount": 4900,
  "max_amount": 49900,
  "amount_type": "VARIABLE",
  "frequency": "MONTHLY",
  "trail_days": 7
}'`}</Code>
        <p style={s.p}>
          The response is PhonePe's, plus the <code style={s.inline}>subscription_id</code> AutoRev
          assigned — a 24-character hex string you cannot choose. Send the payer to the intent URL
          it carries.
        </p>
        <p style={s.pMuted}>
          The setup order expires <strong>10 minutes</strong> after creation. If the payer does
          not act, start a new setup rather than retrying the old subscription ID.
        </p>
      </Section>

      <Section id="status" title="Subscription Status" method="GET" path={`${base}/status/{subscription_id}`}>
        <p style={s.p}>
          Reads the mandate live from PhonePe, saves the result, and returns PhonePe's response.
          No query parameters.
        </p>
        <p style={s.p}>
          Poll this after setup to find out whether the payer approved. It is also what promotes
          an approved mandate to <code style={s.inline}>ACTIVE</code> and computes its first
          payment date, so polling does real work rather than just reporting.
        </p>
        <Code>{`curl "${base}/status/<subscription_id>" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</Code>
        <p style={s.pMuted}>
          Returns <code style={s.inline}>404</code> if the subscription is not yours,{" "}
          <code style={s.inline}>502</code> if PhonePe is unreachable. Polling suits the setup
          window; for the recurring life of a mandate, webhooks cost less than a poll loop.
        </p>
      </Section>

      <Section id="user-status" title="User Status" method="GET" path={`${base}/{phone_number}/status`}>
        <p style={s.p}>
          The single best call for "is this customer subscribed?". Takes a 10-digit number and no
          query parameters. Where a number holds several subscriptions it picks one, preferring{" "}
          <code style={s.inline}>ACTIVE</code>, then mid-setup, then{" "}
          <code style={s.inline}>PAUSED</code>/<code style={s.inline}>REVOKED</code>. An unknown
          number returns <code style={s.inline}>{`{"state": "NOT_FOUND"}`}</code>.
        </p>
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Field</th>
                <th style={s.th}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["state", "Subscription state, or NOT_FOUND"],
                ["subscription_id", ""],
                ["subscription_start_date", "First billing date — setup date plus trail_days"],
                ["subscription_validity_end_date", "Paid up to, including a 6-day grace period"],
                ["subscription_frequency", ""],
                ["next_payment_date", "YYYY-MM-DD, or null for ON_DEMAND"],
                ["last_payment_status", "COMPLETED, FAILED, PENDING, or null"],
                ["number_of_successful_payments", ""],
                ["payment_amount", "The mandate's max_amount, in paise"],
                ["trail_amount", "Trial charge, or null when there is no trial"],
                ["trail_status", "ACTIVE or EXPIRED during a trial, else null"],
              ].map(([f, n]) => (
                <tr key={f} style={s.tr}>
                  <td style={s.td}><code style={s.param}>{f}</code></td>
                  <td style={{ ...s.td, color: "#475569" }}>{n || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={s.callout}>
          To gate access, read <code style={s.inline}>subscription_validity_end_date</code>, not{" "}
          <code style={s.inline}>state</code>. A mandate can be <code style={s.inline}>REVOKED</code>{" "}
          while the customer is still inside a period they already paid for — cutting them off on
          state alone takes away time they bought.
        </div>
        <Code>{`curl "${base}/9876543210/status" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</Code>
        <p style={s.pMuted}>
          This answers for one subscription. For every subscription a number holds, see{" "}
          <a href="#user-subscriptions" style={s.link}>User Subscriptions</a>; for what they have
          actually paid, see <a href="#invoices" style={s.link}>Invoices</a>.
        </p>
      </Section>

      <Section
        id="user-subscriptions"
        title="User Subscriptions"
        method="GET"
        path={`${base}/{phone_number}/subscriptions`}
      >
        <p style={s.p}>
          Every subscription a phone number holds, newest first, each with its collection
          attempts. No query parameters. Use this when one answer is not enough — a customer who
          resubscribed, or a support question about which mandate is charging them.
        </p>
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr><th style={s.th}>Field</th><th style={s.th}>Notes</th></tr>
            </thead>
            <tbody>
              {[
                ["id", "The subscription ID"],
                ["phone_number", ""],
                ["amount", "Paise"],
                ["frequency", ""],
                ["state", ""],
                ["next_payment_date", "Epoch milliseconds — not the YYYY-MM-DD used elsewhere"],
                ["total_payments", "Successful collections so far"],
                ["created_at", "ISO 8601"],
                ["txns", "Array of attempts: id, amount, state, transaction_id, created_at"],
              ].map(([f, n]) => (
                <tr key={f} style={s.tr}>
                  <td style={s.td}><code style={s.param}>{f}</code></td>
                  <td style={{ ...s.td, color: "#475569" }}>{n || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={s.callout}>
          <code style={s.inline}>next_payment_date</code> is <strong>epoch milliseconds</strong>{" "}
          here, while User Status returns the same field as{" "}
          <code style={s.inline}>YYYY-MM-DD</code>. Parsing one as the other is the easiest
          mistake to make against this API.
        </div>
        <Code>{`curl "${base}/9876543210/subscriptions" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</Code>
        <p style={s.pMuted}>
          An unknown number returns an empty <code style={s.inline}>subscriptions</code> array,
          not a 404.
        </p>
      </Section>

      <Section
        id="invoices"
        title="Invoices"
        method="GET"
        path={`${base}/{phone_number}/invoices`}
      >
        <p style={s.p}>
          Every payment a number has actually made, oldest first, across all of its
          subscriptions. No query parameters. This is the billing history to show a customer or
          reconcile against.
        </p>
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr><th style={s.th}>Field</th><th style={s.th}>Notes</th></tr>
            </thead>
            <tbody>
              {[
                ["invoice_id", "First 5 characters of the subscription ID plus a per-subscription sequence number"],
                ["phone_number", ""],
                ["subscription_id", "Which mandate this payment belongs to"],
                ["transaction_id", "PhonePe's transaction ID; for a setup charge, the subscription ID"],
                ["invoice_date", "Scheduled date of the payment (YYYY-MM-DD)"],
                ["payment_date", "When it actually settled (YYYY-MM-DD)"],
                ["amount", "Paise"],
                ["type", "SETUP for the charge taken at signup, RECURRING after"],
              ].map(([f, n]) => (
                <tr key={f} style={s.tr}>
                  <td style={s.td}><code style={s.param}>{f}</code></td>
                  <td style={{ ...s.td, color: "#475569" }}>{n || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={s.p}>
          The response wraps them: <code style={s.inline}>{`{"phone_number", "count", "invoices": [...]}`}</code>.
        </p>
        <div style={s.callout}>
          The <code style={s.inline}>SETUP</code> row is money collected at signup, which never
          becomes a transaction of its own — so its{" "}
          <code style={s.inline}>transaction_id</code> is the subscription ID rather than a
          PhonePe transaction. Counting only{" "}
          <code style={s.inline}>RECURRING</code> rows undercounts what the customer paid by one.
        </div>
        <Code>{`curl "${base}/9876543210/invoices" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</Code>
        <p style={s.pMuted}>
          An unknown number returns <code style={s.inline}>count: 0</code> and an empty array.
        </p>
      </Section>

      <Section id="revoke" title="Revoke" method="POST" path={`${base}/revoke/{subscription_id}`}>
        <p style={s.p}>
          Cancels the mandate at PhonePe. No body, no query parameters.
        </p>
        <Code>{`curl -X POST \\
  "${base}/revoke/<subscription_id>" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</Code>
        <Code label="response">{`{"subscription_id": "b3f1c8d9e4a24f7b8c1d2e3f4a5b6c7d", "state": "REVOKED"}`}</Code>
        <p style={s.p}>
          The <code style={s.inline}>state</code> is whatever PhonePe reports, not an assumption.
          A cancellation can land on <code style={s.inline}>REVOKE_IN_PROGRESS</code> and settle to{" "}
          <code style={s.inline}>REVOKED</code> minutes later, so wait for the{" "}
          <code style={s.inline}>subscription.revoked</code> webhook or re-read the status before
          telling the customer it is done.
        </p>
        <div style={s.callout}>
          This is terminal. Nothing further is collected and there is no un-revoke — the customer
          has to go through setup again.
        </div>
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr><th style={s.th}>Response</th><th style={s.th}>Meaning</th></tr>
            </thead>
            <tbody>
              {[
                ["404 Subscription not found", "Unknown ID, or it belongs to another app"],
                ["409 Subscription is already CANCELLED/REVOKED", "Checked before calling PhonePe, so a repeat is cheap"],
                ["409 PhonePe rejected the cancellation", "PhonePe disagrees about the mandate's state"],
                ["502 PhonePe error", "PhonePe unreachable; nothing changed"],
              ].map(([c, m]) => (
                <tr key={c} style={s.tr}>
                  <td style={s.td}><code style={s.param}>{c}</code></td>
                  <td style={{ ...s.td, color: "#475569" }}>{m}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section id="report" title="Report" method="GET" path={`${base}/report`}>
        <p style={s.p}>
          Counts of subscriptions created and transactions recorded in a time window, grouped by
          state. Useful for a daily reconciliation job or a health dashboard.
        </p>
        <Params
          rows={[
            { name: "start", type: "ISO datetime (IST)", required: false, def: "one hour before end", notes: "Inclusive" },
            { name: "end", type: "ISO datetime (IST)", required: false, def: "now", notes: "Exclusive" },
          ]}
        />
        <Code>{`# default window: the last hour
curl "${base}/report" \\
  -H "Authorization: Bearer YOUR_API_KEY"

# an explicit day, IST
curl "${base}/report?start=2026-10-15T00:00:00&end=2026-10-16T00:00:00" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</Code>
        <Code label="response">{`{
  "window_start": "2026-10-15T00:00:00+05:30",
  "window_end":   "2026-10-16T00:00:00+05:30",
  "app_id": "${appId}",
  "subscriptions_created": { "total": 240, "by_state": { "ACTIVE": 180, "FAILED": 60 } },
  "transactions":          { "total": 310, "by_state": { "COMPLETED": 290, "FAILED": 20 } }
}`}</Code>
        <div style={s.callout}>
          Both figures are windowed on <strong>creation</strong> time, and each row is reported in
          whatever state it holds right now. A transaction created inside the window but settled
          after it still appears under this window. So a one-hour report shows almost no{" "}
          <code style={s.inline}>COMPLETED</code> — PhonePe requires 24 hours' notice before a
          debit, so nothing created in the last hour can have collected yet. Compare days, not
          minutes.
        </div>
      </Section>

      <Section id="errors" title="Errors">
        <p style={s.p}>
          Every error is JSON: <code style={s.inline}>{`{"detail": "<message>"}`}</code>.
        </p>
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr><th style={s.th}>Status</th><th style={s.th}>Meaning</th><th style={s.th}>Retry?</th></tr>
            </thead>
            <tbody>
              {[
                ["400", "Validation failed or a required value is missing", "No — fix the request"],
                ["401", "Missing or wrong API key", "No"],
                ["404", "No such app, subscription or order under this app", "No"],
                ["409", "Conflicts with current state, e.g. already revoked", "No — re-read first"],
                ["410", "Endpoint withdrawn", "No"],
                ["422", "Body failed schema validation; the message names the field", "No"],
                ["500", "Unexpected failure; nothing was created", "Yes"],
                ["502", "PhonePe errored or was unreachable", "Yes, with backoff"],
              ].map(([c, m, r]) => (
                <tr key={c} style={s.tr}>
                  <td style={s.td}><code style={s.param}>{c}</code></td>
                  <td style={{ ...s.td, color: "#475569" }}>{m}</td>
                  <td style={{ ...s.td, color: r.startsWith("Yes") ? "#166534" : "#64748b" }}>{r}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={s.callout}>
          A setup call that returns <code style={s.inline}>502</code> may still have created a
          mandate at PhonePe. Check{" "}
          <code style={s.inline}>GET /{"{phone_number}"}/subscriptions</code> before retrying, or
          the customer can end up with two.
        </div>
      </Section>
    </div>
  );
}

const s = {
  page: { maxWidth: 900 },
  back: { padding: "6px 12px", backgroundColor: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#475569", marginBottom: 18 },
  h1: { margin: "0 0 8px", fontSize: 26, color: "#1e293b" },
  lead: { margin: "0 0 20px", fontSize: 14, color: "#475569", lineHeight: 1.6 },
  toc: { display: "flex", flexWrap: "wrap", gap: 8, padding: "12px 14px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, marginBottom: 28 },
  tocLink: { fontSize: 13, color: "#4f46e5", textDecoration: "none", padding: "3px 9px", borderRadius: 6, backgroundColor: "#fff", border: "1px solid #e2e8f0" },
  section: { backgroundColor: "#fff", borderRadius: 12, padding: 24, marginBottom: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.07)", scrollMarginTop: 16 },
  h2: { margin: "0 0 12px", fontSize: 18, color: "#1e293b" },
  endpointRow: { display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, marginBottom: 16, flexWrap: "wrap" },
  endpoint: { fontSize: 12.5, color: "#1e293b", wordBreak: "break-all" },
  method: { fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 5, letterSpacing: "0.04em", flexShrink: 0 },
  post: { backgroundColor: "#ede9fe", color: "#4f46e5" },
  get: { backgroundColor: "#dcfce7", color: "#166534" },
  p: { margin: "0 0 12px", fontSize: 14, color: "#334155", lineHeight: 1.65 },
  pMuted: { margin: "0 0 12px", fontSize: 13, color: "#64748b", lineHeight: 1.6 },
  link: { color: "#4f46e5", textDecoration: "none", fontWeight: 500 },
  inline: { backgroundColor: "#f1f5f9", padding: "1px 5px", borderRadius: 4, fontSize: 12.5, color: "#1e293b" },
  callout: { padding: "12px 14px", backgroundColor: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, fontSize: 13.5, color: "#713f12", lineHeight: 1.6, margin: "0 0 16px" },
  tableWrap: { border: "1px solid #e2e8f0", borderRadius: 8, overflowX: "auto", marginBottom: 16 },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 560 },
  th: { textAlign: "left", padding: "9px 12px", fontSize: 11, fontWeight: 600, color: "#64748b", borderBottom: "1px solid #f1f5f9", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" },
  tr: { borderBottom: "1px solid #f8fafc" },
  td: { padding: "9px 12px", fontSize: 13, color: "#1e293b", verticalAlign: "top" },
  param: { fontSize: 12, color: "#1e293b", whiteSpace: "nowrap" },
  reqYes: { fontSize: 11, fontWeight: 600, color: "#991b1b", backgroundColor: "#fef2f2", border: "1px solid #fecaca", padding: "1px 7px", borderRadius: 20, whiteSpace: "nowrap" },
  reqNo: { fontSize: 11, color: "#475569", backgroundColor: "#f1f5f9", border: "1px solid #e2e8f0", padding: "1px 7px", borderRadius: 20, whiteSpace: "nowrap" },
  codeWrap: { backgroundColor: "#0f172a", borderRadius: 8, overflow: "hidden", marginBottom: 16 },
  codeHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 14px", borderBottom: "1px solid #1e293b" },
  codeLabel: { fontSize: 10.5, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" },
  codeCopy: { padding: "3px 10px", backgroundColor: "#1e293b", border: "1px solid #334155", color: "#94a3b8", borderRadius: 5, cursor: "pointer", fontSize: 11 },
  pre: { margin: 0, padding: "14px 16px", fontSize: 12.5, lineHeight: 1.65, color: "#e2e8f0", overflowX: "auto", fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', monospace", whiteSpace: "pre" },
};
